/**
 * A Flow session that expires while the page is open (the authtool library's RedirectToLogin only checks at load).
 *
 * index.jsx wraps the global fetch with `watchSessionExpiry`: a 401 from Flow's own APIs (same origin: /api/, /sandbox/,
 * /bff/api/...) asks authtool-bff once whether the session is still there (`GET /bff/session`). Only when that says no is
 * the expiry announced (`onSessionExpired`); SessionExpiredNotice then tells the person and sends them to sign in, back to
 * the same page. A 401 of one API while the session is fine (a missing token for one service) announces nothing.
 * Every response is handed back untouched.
 */
const EXPIRED_EVENT = 'flow:session-expired';
const REDIRECT_AT_KEY = 'flow.sessionExpiredRedirectAt';
// Paths whose 401 says nothing about Flow's session (the sign-in flow itself).
const IGNORED = /^\/bff\/(?:login|logout|session|oauth|oidc)/;

let checking = null;
let expired = false;

function pathOf(input) {
  const raw = typeof input === 'string' ? input : (input?.url || (input instanceof URL ? input.href : ''));
  try {
    const url = new URL(raw, window.location.origin);
    return url.origin === window.location.origin ? url.pathname : null;
  } catch {
    return null;
  }
}

/** Whether a 401 for this request may mean the session is gone: a same-origin API call, not the sign-in flow. */
export function watchesPath(input) {
  const path = pathOf(input);
  return Boolean(path) && !IGNORED.test(path) && /^\/(?:api|sandbox|bff)\//.test(path);
}

/** The sign-in URL that comes back to this page. */
export function loginUrl(href = window.location.href) {
  return `/bff/login?redirect_uri=${encodeURIComponent(href)}`;
}

/** Whether Flow sent the person to sign in less than a minute ago (then a click, not another automatic redirect). */
export function redirectedRecently(now = Date.now()) {
  try {
    const at = Number(window.sessionStorage.getItem(REDIRECT_AT_KEY));
    return Number.isFinite(at) && now - at < 60000;
  } catch {
    return false;
  }
}

/** Goes to sign in, noting the time so a session that cannot be restored never loops. */
export function goToLogin() {
  try { window.sessionStorage.setItem(REDIRECT_AT_KEY, String(Date.now())); } catch { /* storage blocked: still go */ }
  window.location.assign(loginUrl());
}

/** Calls `listener()` once the session is found expired; returns the unsubscribe. */
export function onSessionExpired(listener) {
  const handle = () => listener();
  window.addEventListener(EXPIRED_EVENT, handle);
  if (expired) { listener(); }
  return () => window.removeEventListener(EXPIRED_EVENT, handle);
}

async function confirmExpired() {
  if (expired) { return; }
  if (!checking) {
    // The page's own fetch, at call time: /bff/session is never watched, so this cannot loop.
    checking = window.fetch('/bff/session', { credentials: 'include', headers: { Accept: 'application/json' } })
      .then((response) => response.status === 401 || response.status === 403)
      .catch(() => false) // offline or the bff restarting: not an expiry
      .finally(() => { checking = null; });
  }
  if (await checking && !expired) {
    expired = true;
    window.dispatchEvent(new CustomEvent(EXPIRED_EVENT));
  }
}

/** `fetchImpl`, plus: a 401 from Flow's own APIs checks the session once and announces an expiry. */
export function watchSessionExpiry(fetchImpl) {
  return async(input, init) => {
    const response = await fetchImpl(input, init);
    if (response?.status === 401 && watchesPath(input)) { confirmExpired(); }
    return response;
  };
}

/** Tests only: forget a previous expiry. */
export function resetSessionExpiry() { expired = false; checking = null; }
